import {Link} from 'react-router-dom';
import {Grid, Table, Tag, Tooltip} from 'antd';
import {MessageOutlined} from '@ant-design/icons';
import {CsatStars} from '../components/CsatStars';
import {Explained} from './Info';
import {customerName, formatAge, formatDuration} from './performanceUtils';
import styles from './performance.module.scss';

const STATUS_COLOURS = {
  RESOLVED: 'green',
  ABANDONED: 'default',
  ESCALATED: 'red',
  HUMAN_ACTIVE: 'orange',
  AI_ACTIVE: 'purple',
  GUIDED_FLOW: 'blue',
};

const stamp = iso =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      })
    : null;

const ticketRef = id => `TKT-${String(id).slice(0, 8).toUpperCase()}`;

const StatusTag = ({status}) => (
  <Tag color={STATUS_COLOURS[status] || 'default'} className={styles.statusTag}>
    {String(status || '—')
      .replace(/_/g, ' ')
      .toLowerCase()}
  </Tag>
);

const Topic = ({ticket}) => (
  <div className={styles.twoLine}>
    <span className={styles.topicName}>
      {ticket.category || 'Not categorised'}
    </span>
    {/* Issues can be long, often French; two lines, the rest on hover. */}
    <span className={styles.clamp2} title={ticket.issue || undefined}>
      {ticket.issue || '—'}
    </span>
  </div>
);

const Dates = ({ticket}) => (
  <div className={styles.twoLine}>
    <span>
      <span className={styles.dateLabel}>Opened</span>{' '}
      {stamp(ticket.openedAt) || '—'}
    </span>
    <span>
      <span className={styles.dateLabel}>Resolved</span>{' '}
      {stamp(ticket.resolvedAt) || (
        <span className={styles.subtle}>not yet</span>
      )}
    </span>
  </div>
);

const Rating = ({ticket, showComment}) => (
  <span className={styles.ratingCell}>
    <CsatStars score={ticket.rating ?? null} />
    {showComment && ticket.comment && (
      // Plain text: rendered as a string, never as markup.
      <span className={styles.commentText}>{ticket.comment}</span>
    )}
    {!showComment && ticket.comment && (
      // Plain text: shown as a string, never as markup.
      <Tooltip title={ticket.comment}>
        <MessageOutlined
          className={styles.commentIcon}
          aria-label={`Customer comment: ${ticket.comment}`}
        />
      </Tooltip>
    )}
  </span>
);

const isPending = ticket => !['RESOLVED', 'ABANDONED'].includes(ticket.status);

const hasValue = value => value !== null && value !== undefined;

// Resolved: calendar resolution time. Still open: how old it is now.
const Time = ({ticket}) => {
  if (isPending(ticket)) {
    return (
      <span className={styles.twoLine}>
        <span>{formatAge(ticket.openSeconds)}</span>
        <span className={styles.smallNote}>open now</span>
      </span>
    );
  }
  if (!hasValue(ticket.customerWaitSeconds)) {
    return <span className={styles.subtle}>—</span>;
  }
  return (
    <span className={styles.twoLine}>
      <span>{formatDuration(ticket.customerWaitSeconds)}</span>
      {hasValue(ticket.agentSeconds) && (
        <span className={styles.smallNote}>
          {formatDuration(ticket.agentSeconds)} with the team
        </span>
      )}
    </span>
  );
};

/**
 * The agent's tickets: opened, resolved or abandoned in the period, plus all
 * pending now. Six columns that fit beside the sidebar; on a phone the rows
 * become cards rather than a table that scrolls sideways.
 */
export const TicketList = ({tickets, loading, emptyText, showComments}) => {
  const screens = Grid.useBreakpoint();
  const rows = tickets || [];

  if (screens.md === false) {
    if (!rows.length) {
      return (
        <p className={styles.subtle}>
          {loading
            ? 'Loading tickets…'
            : emptyText || 'No tickets on this page'}
        </p>
      );
    }
    return (
      <ul className={styles.ticketCards}>
        {rows.map(ticket => (
          <li key={ticket.id} className={styles.ticketCard}>
            <div className={styles.ticketCardHead}>
              <Link to={`/dashboard/ai-helpdesk/ticket/${ticket.id}`}>
                {ticketRef(ticket.id)}
              </Link>
              <StatusTag status={ticket.status} />
            </div>
            <span className={styles.smallNote}>
              {customerName(ticket.customer)}
            </span>
            <Topic ticket={ticket} />
            <Dates ticket={ticket} />
            <div className={styles.ticketCardFoot}>
              <Time ticket={ticket} />
              <Rating ticket={ticket} showComment={showComments} />
            </div>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <Table
      size="middle"
      rowKey="id"
      tableLayout="fixed"
      loading={loading}
      pagination={false}
      dataSource={rows}
      locale={{emptyText: emptyText || 'No tickets on this page'}}
      columns={[
        {
          title: 'Ticket',
          key: 'id',
          width: 130,
          render: (_, ticket) => (
            <div className={styles.twoLine}>
              <Link to={`/dashboard/ai-helpdesk/ticket/${ticket.id}`}>
                {ticketRef(ticket.id)}
              </Link>
              <StatusTag status={ticket.status} />
            </div>
          ),
        },
        {
          title: 'Customer',
          key: 'customer',
          width: 120,
          render: (_, ticket) => (
            <span className={styles.clamp2}>
              {customerName(ticket.customer)}
            </span>
          ),
        },
        {
          title: 'Topic',
          key: 'topic',
          render: (_, ticket) => <Topic ticket={ticket} />,
        },
        {
          title: 'Dates (UTC)',
          key: 'dates',
          width: 175,
          render: (_, ticket) => <Dates ticket={ticket} />,
        },
        {
          title: (
            <Explained text="Resolved: calendar time from opening to resolution, and how much of it was with the support team. Still open: how long ago it was opened.">
              Time
            </Explained>
          ),
          key: 'time',
          width: 140,
          align: 'right',
          render: (_, ticket) => <Time ticket={ticket} />,
        },
        {
          title: (
            <Explained text="The stars the customer gave, if they rated it. The speech bubble shows their comment.">
              Rating
            </Explained>
          ),
          key: 'rating',
          width: showComments ? 260 : 130,
          render: (_, ticket) => (
            <Rating ticket={ticket} showComment={showComments} />
          ),
        },
      ]}
    />
  );
};
